import { type IncludedFinalGrade } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CircularProgress, Stack } from "@mui/material";
import { environmentConfig } from "@src/appV2/environment";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import {
  BottomSheet,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { WorkBadgeReviewPolicySource } from "@src/appV2/redesign/WorkBadges/components/Policy/types";
import { WorkBadgeReviewPolicyStep } from "@src/appV2/redesign/WorkBadges/hooks/useWorkBadgeReviewPolicy";
import { getWorkBadgeReviewPolicyUrl } from "@src/appV2/redesign/WorkBadges/utils/getWorkBadgeReviewPolicyUrl";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import SwipeableViews from "react-swipeable-views";

import { useGetBadgeVerificationPhotoUrl } from "./api/useGetBadgeVerificationPhotoUrl";
import { useMarkBadgeVerificationResultSeen } from "./api/useMarkBadgeVerificationResultSeen";

const SAMPLE_BADGE_IMAGE_URL = `${environmentConfig.REACT_APP_PUBLIC_ASSETS_BASE_URL}/images/badge-policy/idealBadgeSelfie.png`;

interface BadgeVerificationResultBottomSheetProps {
  modalState: UseModalState;
  verificationId?: string;
  shiftId?: string;
  qualificationName?: string;
  finalGrade?: IncludedFinalGrade;
}

export function BadgeVerificationResultBottomSheet(props: BadgeVerificationResultBottomSheetProps) {
  const { modalState, verificationId, shiftId, qualificationName, finalGrade } = props;

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const history = useHistory();

  const hasRequiredData = isDefined(verificationId) && isDefined(shiftId) && isDefined(finalGrade);

  const { data: photoUrlData, isLoading: isPhotoLoading } =
    useGetBadgeVerificationPhotoUrl(verificationId);
  const {
    mutateAsync: markSeen,
    isLoading: isMarkingAsSeen,
    isError: isPhotoError,
  } = useMarkBadgeVerificationResultSeen();

  const photoUrl = photoUrlData?.data.attributes.downloadUrl;
  const notes = finalGrade?.attributes.notes;

  useLogEffect(
    APP_V2_USER_EVENTS.BADGE_VERIFICATION_RESULT_VIEWED,
    { shiftId, verificationId },
    { enabled: modalState.modalIsOpen && hasRequiredData }
  );

  const handleGetNewBadge = useCallback(async () => {
    if (isMarkingAsSeen || !isDefined(verificationId) || !isDefined(shiftId)) {
      return;
    }

    try {
      logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RESULT_GET_NEW_BADGE_CLICKED, {
        shiftId,
        verificationId,
      });

      await markSeen({ verificationId, shiftId });
      modalState.closeModal();

      history.push(
        getWorkBadgeReviewPolicyUrl({
          source: WorkBadgeReviewPolicySource.BADGE_VERIFICATION,
          shiftId,
          qualificationName: qualificationName ?? "",
          step: isDefined(qualificationName)
            ? WorkBadgeReviewPolicyStep.SELECT_HEADSHOT
            : WorkBadgeReviewPolicyStep.REVIEW_POLICY,
        })
      );
    } catch {
      // Error logging and user toast are handled by the mutation's meta config
    }
  }, [shiftId, verificationId, markSeen, isMarkingAsSeen, modalState, history, qualificationName]);

  const handleAcknowledge = useCallback(async () => {
    if (isMarkingAsSeen || !isDefined(verificationId) || !isDefined(shiftId)) {
      return;
    }

    try {
      logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RESULT_DISMISSED, {
        shiftId,
        verificationId,
      });

      await markSeen({ verificationId, shiftId });
      modalState.closeModal();
    } catch {
      // Error logging and user toast are handled by the mutation's meta config
    }
  }, [shiftId, verificationId, markSeen, isMarkingAsSeen, modalState]);

  useEffect(() => {
    if (!isPhotoLoading && !isDefined(photoUrl) && !isPhotoError) {
      logError(APP_V2_APP_EVENTS.BADGE_VERIFICATION_GET_FAILURE, {
        error: "Badge verification photo URL is undefined after loading",
        metadata: { verificationId, shiftId },
      });
    }
  }, [isPhotoLoading, photoUrl, verificationId, shiftId, isPhotoError]);

  if (!hasRequiredData) {
    return null;
  }

  return (
    <BottomSheet
      canDismiss={false}
      modalState={modalState}
      header={<BottomSheetHeader onClose={modalState.closeModal} />}
      paperSx={(theme) => ({
        background: theme.palette.shift?.badgeSkippedWarning.gradient,
      })}
    >
      <Stack spacing={8} sx={{ paddingX: 6, paddingBottom: 2 }}>
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Title variant="h2" component="h2" sx={(theme) => ({ color: theme.palette.error.light })}>
            Your badge has not been verified
          </Title>
          {isDefined(notes) && (
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.error.light })}>
              {notes}
            </Text>
          )}
        </Stack>

        {isPhotoLoading && (
          <Stack alignItems="center" sx={{ paddingY: 6 }}>
            <CircularProgress size={32} />
          </Stack>
        )}

        {!isPhotoLoading && isDefined(photoUrl) && (
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                width: "100%",
                maxWidth: 300,
                alignSelf: "center",
              }}
            >
              <SwipeableViews
                enableMouseEvents
                index={activeImageIndex}
                onChangeIndex={setActiveImageIndex}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={photoUrl}
                    alt="Your badge photo"
                    sx={(theme) => ({
                      width: "100%",
                      maxWidth: 280,
                      height: 280,
                      objectFit: "cover",
                      borderRadius: theme.borderRadius?.xSmall,
                    })}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={SAMPLE_BADGE_IMAGE_URL}
                    alt="Example of a worker wearing a badge"
                    sx={(theme) => ({
                      width: "100%",
                      maxWidth: 280,
                      height: 280,
                      objectFit: "cover",
                      objectPosition: "center bottom",
                      borderRadius: theme.borderRadius?.xSmall,
                    })}
                  />
                </Box>
              </SwipeableViews>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="center">
              {[0, 1].map((index) => (
                <Box
                  key={index}
                  sx={(theme) => ({
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor:
                      index === activeImageIndex
                        ? theme.palette.background.invert
                        : theme.palette.grey[300],
                    transition: theme.transitions.create("background-color"),
                  })}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {!isPhotoLoading && !isDefined(photoUrl) && (
          <Text
            variant="body2"
            color={(theme) => theme.palette.text.secondary}
            sx={{ textAlign: "center" }}
          >
            Your image couldn't be loaded, please contact support with any questions
          </Text>
        )}

        <Text
          variant="body2"
          color={(theme) => theme.palette.text.primary}
          sx={{ textAlign: "center" }}
        >
          Missing badge verification 3 times within 60 days will restrict you from badge-required
          workplaces for 30 days
        </Text>
      </Stack>

      <DialogFooter orientation="vertical">
        <Button
          fullWidth
          variant="outlined"
          size="large"
          disabled={isMarkingAsSeen}
          onClick={handleGetNewBadge}
        >
          Get a new badge
        </Button>
        <LoadingButton
          fullWidth
          variant="text"
          size="large"
          isLoading={isMarkingAsSeen}
          onClick={handleAcknowledge}
        >
          Okay
        </LoadingButton>
      </DialogFooter>
    </BottomSheet>
  );
}
